import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { navigation } from '@carrent/platform'
import { authApi, getAccessToken, getRefreshToken, clearTokens } from '@carrent/api'
import { ConsoleLayout } from './layout/ConsoleLayout'

import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'

import AdminDashboard from './pages/admin/AdminDashboard'
import InfoPanelPage from './pages/admin/infopanel/InfoPanelPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminUserDetail from './pages/admin/AdminUserDetail'
import AdminCarsPage from './pages/admin/AdminCarsPage'
import AdminCarDetail from './pages/admin/AdminCarDetail'
import AdminCarFinance from './pages/admin/AdminCarFinance'
import AdminTripsPage from './pages/admin/AdminTripsPage'
import AdminTripDetail from './pages/admin/AdminTripDetail'
import AdminRemindersPage from './pages/admin/AdminRemindersPage'
import AdminChatsPage from './pages/admin/AdminChatsPage'
import AdminTicketsPage from './pages/admin/AdminTicketsPage'
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage'
import AdminLogsPage from './pages/admin/AdminLogsPage'
import AdminCalendarPage from './pages/admin/AdminCalendarPage'
import AdminSettingsPage from './pages/admin/AdminSettingsPage'
import AdminRegionsPage from './pages/admin/AdminRegionsPage'
import AdminStructurePage from './pages/admin/structure/AdminStructurePage'
import AdminRegionDetail from './pages/admin/AdminRegionDetail'
import TicketChat from './pages/common/TicketChat'
import KnowledgePage from './pages/admin/knowledge/KnowledgePage'
import GroupsPage from './pages/admin/groups/GroupsPage'

import TasksPage from './pages/tasks/TasksPage'
import TaskCreatePage from './pages/tasks/TaskCreatePage'
import TaskDetailPage from './pages/tasks/TaskDetailPage'
import VideoCallsPage from './pages/video/VideoCallsPage'
import VideoCallPage from './pages/video/VideoCallPage'
import GuestCallPage from './pages/GuestCallPage'

function FullPageSpinner() {
  return (
    <div className="full-page-center">
      <div className="spinner" />
    </div>
  )
}

/**
 * Консоль доступна только персоналу. Водитель, попавший сюда,
 * отправляется на своё приложение — разные хосты, поэтому это
 * внешний редирект, а не router-навигация.
 */
const DRIVER_APP_URL = import.meta.env.VITE_DRIVER_APP_URL || 'https://app.carflow.site'

function RequireStaff({ children }) {
  const { user, isLoading, isAdmin } = useAuthStore()
  if (isLoading) return <FullPageSpinner />
  if (!user) return <Navigate to="/login" replace />
  if (!isAdmin()) {
    navigation.replaceLocation(DRIVER_APP_URL)
    return <FullPageSpinner />
  }
  return children
}

function RequireGuest({ children }) {
  const { user, isLoading } = useAuthStore()
  if (isLoading) return <FullPageSpinner />
  if (user) return <Navigate to="/admin" replace />
  return children
}

export default function App() {
  const { login, setLoading } = useAuthStore()

  useEffect(() => {
    const token = getAccessToken()
    if (!token) {
      setLoading(false)
      return
    }
    authApi
      .me()
      .then(({ data }) => login(data.data, token, getRefreshToken()))
      .catch(clearTokens)
      .finally(() => setLoading(false))
  }, [])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<RequireGuest><LoginPage /></RequireGuest>} />
        <Route path="/register" element={<RequireGuest><RegisterPage /></RequireGuest>} />
        {/* Гостевая страница звонка: без логина — страховой или
            арендодатель открывает ссылку, не имея аккаунта. */}
        <Route path="/call/:id" element={<GuestCallPage />} />

        <Route path="/admin" element={<RequireStaff><ConsoleLayout /></RequireStaff>}>
          <Route index element={<AdminDashboard />} />
          <Route path="infopanel" element={<InfoPanelPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:id" element={<AdminUserDetail />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="cars" element={<AdminCarsPage />} />
          <Route path="cars/:id" element={<AdminCarDetail />} />
          <Route path="cars/:id/finance" element={<AdminCarFinance />} />
          <Route path="trips" element={<AdminTripsPage />} />
          <Route path="trips/:id" element={<AdminTripDetail />} />
          <Route path="reminders" element={<AdminRemindersPage />} />
          <Route path="chats" element={<AdminChatsPage />} />
          <Route path="tickets" element={<AdminTicketsPage />} />
          <Route path="tickets/:id" element={<TicketChat />} />
          <Route path="kb" element={<KnowledgePage />} />
          <Route path="kb/:spaceKey" element={<KnowledgePage />} />
          <Route path="kb/:spaceKey/:pageId" element={<KnowledgePage />} />
          <Route path="analytics" element={<AdminAnalyticsPage />} />
          <Route path="logs" element={<AdminLogsPage />} />
          <Route path="regions" element={<AdminRegionsPage />} />
          <Route path="structure" element={<AdminStructurePage />} />
          <Route path="regions/:id" element={<AdminRegionDetail />} />
          <Route path="calendar" element={<AdminCalendarPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="video" element={<VideoCallsPage />} />
          <Route path="video/:id" element={<VideoCallPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="tasks/new" element={<TaskCreatePage />} />
          <Route path="tasks/:id" element={<TaskDetailPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
